// =============================================================================
// features/admin/feature-flags/FlagsTable.tsx — Tabela de feature flags com
// filtro por status, toggle inline e ação de audiência (F1-S23, F32-S04).
// Extraído de pages/admin/FeatureFlags.tsx para caber no domínio
// features/admin/feature-flags/** (F32-S04).
// =============================================================================

import * as React from 'react';

import type { FeatureFlagDto, FeatureFlagStatus } from '../../../hooks/admin/useFeatureFlags.types';

import { describeAudienceApplicability } from './audienceImpact';
import { resolveStatusChange } from './statusChange';

type StatusFilter = 'all' | FeatureFlagStatus;

// ---------------------------------------------------------------------------
// Status Badge
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: FeatureFlagStatus }): React.JSX.Element {
  const config: Record<FeatureFlagStatus, { label: string; bg: string; text: string }> = {
    enabled: { label: 'Habilitado', bg: 'var(--success-bg)', text: 'var(--success)' },
    disabled: { label: 'Desabilitado', bg: 'var(--danger-bg)', text: 'var(--danger)' },
    internal_only: { label: 'Interno', bg: 'var(--info-bg)', text: 'var(--info)' },
  };
  const { label, bg, text } = config[status];
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 font-sans text-xs font-medium"
      style={{ backgroundColor: bg, color: text }}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Status Control — três posições (F38-S16)
//
// Substitui o antigo toggle binário. `internal_only` tinha `isEnabled ===
// false`, então o toggle mandava 'enabled' num clique só — promovendo
// silenciosamente uma flag interna para todo mundo. Aqui cada posição é
// explícita, e a decisão de aplicar direto vs. exigir confirmação vem de
// `resolveStatusChange` (statusChange.ts) — a UI nunca decide sozinha.
// ---------------------------------------------------------------------------

const STATUS_OPTIONS: { value: FeatureFlagStatus; label: string; bg: string; text: string }[] = [
  { value: 'disabled', label: 'Off', bg: 'var(--danger-bg)', text: 'var(--danger)' },
  { value: 'internal_only', label: 'Interno', bg: 'var(--info-bg)', text: 'var(--info)' },
  { value: 'enabled', label: 'On', bg: 'var(--success-bg)', text: 'var(--success)' },
];

function StatusControl({
  flagKey,
  currentStatus,
  onRequestChange,
  isPending,
}: {
  flagKey: string;
  currentStatus: FeatureFlagStatus;
  onRequestChange: (flagKey: string, from: FeatureFlagStatus, to: FeatureFlagStatus) => void;
  isPending: boolean;
}): React.JSX.Element {
  return (
    <div
      role="radiogroup"
      aria-label={`Status da flag ${flagKey}`}
      className="inline-flex gap-0.5 rounded-md p-0.5"
      style={{ backgroundColor: 'var(--bg-inset)' }}
    >
      {STATUS_OPTIONS.map((opt) => {
        const isActive = currentStatus === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-label={`Marcar flag ${flagKey} como ${opt.label}`}
            disabled={isPending}
            onClick={() => {
              const decision = resolveStatusChange(currentStatus, opt.value);
              if (decision.kind === 'noop') return;
              onRequestChange(flagKey, currentStatus, opt.value);
            }}
            className="rounded px-2.5 py-1 font-sans text-xs font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/20 disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              backgroundColor: isActive ? opt.bg : 'transparent',
              color: isActive ? opt.text : 'var(--text-3)',
              boxShadow: isActive ? 'var(--elev-1)' : 'none',
              transitionDuration: 'var(--dur-fast)',
              transitionTimingFunction: 'var(--ease)',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Audience applicability badge — "vale" vs "ignorada" vs "guardada" (F38-S16)
// ---------------------------------------------------------------------------

// `--text-4` reprovou no security review (F38-S16 PR #713): contra o fundo
// real onde este hint renderiza (`--bg-elev-1`, não `--bg-inset`) dava
// 2,53:1 no claro e 2,41:1 no escuro — bem abaixo do AA (4,5:1) para o texto
// pequeno (0.68rem) daqui. E o tom mais apagado era exatamente `ignored`, o
// estado de MAIOR exposição do painel (flag `enabled` = audiência 100%
// ignorada, ligado para todos). Medido de novo contra `--bg-elev-1`
// (fórmula de luminância relativa da WCAG):
//   `stored`  → `--text-3`   claro 4,42:1 / escuro 4,80:1 (neutro, sem alarme)
//   `ignored` → `--warning`  claro 5,81:1 / escuro 10,22:1 (o estado de risco
//               precisa parecer estado de risco, não cinza apagado)
// `--text-3` no claro fica a ~0,08 do limiar de 4,5:1 — mesmo padrão já usado
// nos cabeçalhos desta tabela (`Chave`, `Descrição` etc. em text-xs
// uppercase); não é regressão introduzida aqui, é o piso atual do token.
const AUDIENCE_TONE_STYLE: Record<'applies' | 'ignored' | 'stored', { color: string }> = {
  applies: { color: 'var(--info)' },
  ignored: { color: 'var(--warning)' },
  stored: { color: 'var(--text-3)' },
};

function AudienceApplicabilityHint({ status }: { status: FeatureFlagStatus }): React.JSX.Element {
  const badge = describeAudienceApplicability(status);
  const style = AUDIENCE_TONE_STYLE[badge.tone];
  return (
    <p className="font-sans text-[0.68rem] mt-1" style={{ color: style.color }}>
      {badge.label}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Filter Tabs
// ---------------------------------------------------------------------------

export function FilterTabs({
  current,
  counts,
  onChange,
}: {
  current: StatusFilter;
  counts: Record<StatusFilter, number>;
  onChange: (filter: StatusFilter) => void;
}): React.JSX.Element {
  const tabs: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: 'Todas' },
    { value: 'enabled', label: 'Habilitadas' },
    { value: 'disabled', label: 'Desabilitadas' },
    { value: 'internal_only', label: 'Interno' },
  ];
  return (
    <div className="flex gap-1 p-1 rounded-md" style={{ backgroundColor: 'var(--bg-inset)' }}>
      {tabs.map((tab) => {
        const isActive = current === tab.value;
        return (
          <button
            key={tab.value}
            type="button"
            onClick={() => onChange(tab.value)}
            className="flex items-center gap-1.5 rounded px-3 py-1.5 font-sans text-sm font-medium transition-all"
            style={{
              backgroundColor: isActive ? 'var(--bg-elev-1)' : 'transparent',
              color: isActive ? 'var(--text)' : 'var(--text-3)',
              boxShadow: isActive ? 'var(--elev-1)' : 'none',
              transitionDuration: 'var(--dur-fast)',
              transitionTimingFunction: 'var(--ease)',
            }}
          >
            {tab.label}
            <span
              className="rounded-full px-1.5 py-0.5 font-mono text-xs"
              style={{
                backgroundColor: isActive ? 'var(--surface-muted)' : 'transparent',
                color: isActive ? 'var(--text-2)' : 'var(--text-4)',
              }}
            >
              {counts[tab.value]}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tabela
// ---------------------------------------------------------------------------

interface FlagsTableProps {
  flags: FeatureFlagDto[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onRequestStatusChange: (flagKey: string, from: FeatureFlagStatus, to: FeatureFlagStatus) => void;
  togglePendingKey: string | null;
  onEditAudience: (flag: FeatureFlagDto) => void;
}

export function FlagsTable({
  flags,
  isLoading,
  isError,
  onRetry,
  onRequestStatusChange,
  togglePendingKey,
  onEditAudience,
}: FlagsTableProps): React.JSX.Element {
  return (
    <div
      className="rounded-lg border overflow-hidden"
      style={{
        borderColor: 'var(--border)',
        boxShadow: 'var(--elev-2)',
        backgroundColor: 'var(--bg-elev-1)',
      }}
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <p className="font-sans text-sm" style={{ color: 'var(--text-3)' }}>
            Carregando flags…
          </p>
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16">
          <p className="font-sans text-sm" style={{ color: 'var(--danger)' }}>
            Erro ao carregar feature flags.
          </p>
          <button
            type="button"
            onClick={onRetry}
            className="rounded-sm border border-border-strong px-4 py-2 font-sans text-sm font-semibold text-ink-2 transition-[border-color,color] duration-fast ease hover:border-azul hover:text-azul focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/20"
          >
            Tentar novamente
          </button>
        </div>
      ) : (
        <table className="w-full">
          <thead>
            <tr style={{ borderBottom: `1px solid var(--border-subtle)` }}>
              {['Chave', 'Descrição', 'Status', 'Visível na UI', 'Mudar status', 'Audiência'].map(
                (h) => (
                  <th
                    key={h}
                    className="px-5 py-3 text-left font-sans text-xs font-semibold uppercase tracking-wider"
                    style={{ color: 'var(--text-3)' }}
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {flags.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center">
                  <p className="font-sans text-sm" style={{ color: 'var(--text-3)' }}>
                    Nenhuma flag encontrada para este filtro.
                  </p>
                </td>
              </tr>
            ) : (
              flags.map((flag, index) => (
                <tr
                  key={flag.key}
                  style={{
                    borderBottom:
                      index < flags.length - 1 ? `1px solid var(--border-subtle)` : 'none',
                    backgroundColor:
                      togglePendingKey === flag.key ? 'var(--surface-hover)' : 'transparent',
                    transition: `background-color var(--dur-fast) var(--ease)`,
                  }}
                >
                  <td className="px-5 py-4">
                    <code
                      className="font-mono text-sm"
                      style={{ color: 'var(--brand-azul)', fontSize: 'var(--text-xs)' }}
                    >
                      {flag.key}
                    </code>
                  </td>
                  <td className="px-5 py-4">
                    <p className="font-sans text-sm" style={{ color: 'var(--text)' }}>
                      {flag.description ?? '—'}
                    </p>
                    {flag.ui_label !== null && (
                      <p className="font-sans text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>
                        Badge: &quot;{flag.ui_label}&quot;
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <StatusBadge status={flag.status} />
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className="font-sans text-sm"
                      style={{ color: flag.visible ? 'var(--success)' : 'var(--text-4)' }}
                    >
                      {flag.visible ? 'Sim' : 'Não'}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <StatusControl
                      flagKey={flag.key}
                      currentStatus={flag.status}
                      onRequestChange={onRequestStatusChange}
                      isPending={togglePendingKey === flag.key}
                    />
                  </td>
                  <td className="px-5 py-4">
                    <button
                      type="button"
                      onClick={() => onEditAudience(flag)}
                      className="inline-flex items-center gap-1.5 rounded-sm border border-border-strong px-3 py-1.5 font-sans text-xs font-semibold text-ink-2 transition-[border-color,box-shadow,color] duration-fast ease hover:border-azul hover:text-azul hover:shadow-e1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/20"
                    >
                      Editar
                    </button>
                    <AudienceApplicabilityHint status={flag.status} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
